/**
 * StatusDropdown.jsx
 * Guard Pill as a clickable toggle → dropdown shows Admin Badge + Live DateTime + MSSQL.
 * Replaces standalone GuardPill + badge-admin + SecurityDateTime separator in the topbar.
 */
import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, ShieldAlert, Database, Clock, ChevronDown } from 'lucide-react';

// Live clock rendered inside the dropdown
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const pad = n => String(n).padStart(2, '0');
  const day = now.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
      <Clock size={12} />
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem' }}>{day} · {time}</span>
    </div>
  );
}

export default function StatusDropdown({ guardStatus, isAdmin, mssqlConnected }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // guardStatus is an object {fileLock, mssqlLock, verified, reason} from /api/health
  const ok = guardStatus?.verified === true || guardStatus?.reason === 'OK' || guardStatus === 'VALID';

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {/* Trigger — Guard Pill */}
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="status-dropdown-trigger"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 9px', borderRadius: 20,
          border: `1px solid ${ok ? 'rgba(22,163,74,0.35)' : 'rgba(220,38,38,0.35)'}`,
          background: ok ? 'rgba(22,163,74,0.10)' : 'rgba(220,38,38,0.10)',
          color: ok ? 'var(--green, #16a34a)' : 'var(--red, #dc2626)',
          fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
          letterSpacing: '0.04em', whiteSpace: 'nowrap',
          transition: 'background 150ms, border-color 150ms',
          userSelect: 'none',
        }}
      >
        {ok
          ? <ShieldCheck size={12} strokeWidth={2.5} />
          : <ShieldAlert size={12} strokeWidth={2.5} />}
        <span className="guard-pill-label">{ok ? 'DNA Verified' : 'SIG ERROR'}</span>
        <ChevronDown size={11} strokeWidth={2.5} style={{ marginLeft: 1, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          data-testid="status-dropdown-panel"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 230, zIndex: 200,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
            padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
            animation: 'dropdown-in 140ms ease',
          }}
        >
          {/* DNA Status row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {ok
              ? <ShieldCheck size={14} color="var(--green, #16a34a)" strokeWidth={2.5} />
              : <ShieldAlert size={14} color="var(--red, #dc2626)" strokeWidth={2.5} />}
            <span style={{
              fontSize: '0.72rem', fontWeight: 700,
              color: ok ? 'var(--green, #16a34a)' : 'var(--red, #dc2626)',
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {ok ? 'DNA Chain Verified' : 'Signature Error'}
            </span>
          </div>
          {/* Lock status pills */}
          {guardStatus && typeof guardStatus === 'object' && (
            <div style={{ display: 'flex', gap: 5 }}>
              <span style={{ fontSize: '0.66rem', padding: '1px 7px', borderRadius: 4, background: guardStatus.fileLock ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)', color: guardStatus.fileLock ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                {guardStatus.fileLock ? '✓' : '✗'} FILE LOCK
              </span>
              <span style={{ fontSize: '0.66rem', padding: '1px 7px', borderRadius: 4, background: guardStatus.mssqlLock ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.12)', color: guardStatus.mssqlLock ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                {guardStatus.mssqlLock ? '✓' : '✗'} MSSQL LOCK
              </span>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />

          {/* Admin badge */}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 4,
                background: 'rgba(239,68,68,0.12)', color: 'var(--red, #dc2626)',
                fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                ◉ Admin Mode
              </span>
            </div>
          )}

          {/* MSSQL status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
            <Database size={12} />
            <span style={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {mssqlConnected ? 'RC26masterDB Connected' : 'File-Only Mode'}
            </span>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
              background: mssqlConnected ? 'var(--green, #16a34a)' : 'var(--text-muted)',
              boxShadow: mssqlConnected ? '0 0 5px rgba(22,163,74,0.6)' : 'none',
            }} />
          </div>

          <div style={{ height: 1, background: 'var(--border)', margin: '2px 0' }} />

          {/* Live clock */}
          <LiveClock />
        </div>
      )}
    </div>
  );
}
